import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Box, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import type { PipelineTargetTable, PipelineJoinNode } from './types';
import TargetChainPanel from './TargetChainPanel';

const MIN_HEIGHT = 120;
const MAX_HEIGHT = 600;
const DEFAULT_HEIGHT = 220;
const COLLAPSED_HEIGHT = 36;

interface PipelineBottomPanelProps {
  sqlByTarget?: Record<string, string>;
  selectedTarget: PipelineTargetTable | null;
  joins?: PipelineJoinNode[];
}

const PipelineBottomPanel: React.FC<PipelineBottomPanelProps> = ({
  sqlByTarget = {},
  selectedTarget,
  joins = [],
}) => {
  // Build tabs dynamically: Chain tab only when target is selected
  const tabs = useMemo(() => {
    const t: string[] = [];
    if (selectedTarget) t.push('Chain');
    // Code tab hidden until backend SQL generation endpoint is fixed
    t.push('Summary');
    return t;
  }, [selectedTarget]);

  const [tabIndex, setTabIndex] = useState(0);
  const [collapsed, setCollapsed] = useState(true);
  const [panelHeight, setPanelHeight] = useState(DEFAULT_HEIGHT); 

  // Drag-to-resize state
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(DEFAULT_HEIGHT);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    startYRef.current = e.clientY;
    startHeightRef.current = panelHeight;

    const handleDragMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = startYRef.current - moveEvent.clientY;
      const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeightRef.current + delta));
      setPanelHeight(newHeight);
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }, [panelHeight]);

  // Auto-switch to Chain tab when a target is selected, back to Code when deselected
  useEffect(() => {
    if (selectedTarget) {
      setTabIndex(0); // Chain is first tab
    } else {
      setTabIndex(0); // Code is first tab when no target
    }
  }, [selectedTarget]);

  const activeSql = useMemo(() => {
    if (selectedTarget && sqlByTarget[selectedTarget.name]) {
      return sqlByTarget[selectedTarget.name];
    }
    const allSql = Object.entries(sqlByTarget)
      .map(([table, sql]) => `-- ══ ${table} ══\n${sql}`)
      .join('\n\n');
    return allSql || '';
  }, [selectedTarget, sqlByTarget]);

  if (collapsed) {
    return (
      <Box
        sx={{
          height: COLLAPSED_HEIGHT,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          bgcolor: '#fafbfc',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {selectedTarget ? `${selectedTarget.name} — ${tabs[tabIndex] ?? 'Chain'}` : 'Summary'}
        </Typography>
        <Tooltip title="Expand panel">
          <IconButton size="small" onClick={() => setCollapsed(false)} sx={{ p: 0.25 }}>
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: panelHeight,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {/* Drag handle for vertical resizing */}
      <Box
        onMouseDown={handleDragStart}
        sx={{
          position: 'absolute',
          top: -4,
          left: 0,
          right: 0,
          height: 8,
          cursor: 'row-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <DragHandleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      </Box>

      {/* Tab bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 1,
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_e, v) => setTabIndex(v)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ flex: 1, minHeight: 32 }}
        >
          {tabs.map((label) => (
            <Tab
              key={label}
              label={label}
              sx={{ minHeight: 32, textTransform: 'none', fontSize: 12, px: 1.5, minWidth: 0 }}
            />
          ))}
        </Tabs>
        <Tooltip title="Collapse panel">
          <IconButton size="small" onClick={() => setCollapsed(true)} sx={{ p: 0.25 }}>
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Chain tab (only when target selected) */}
        {tabs[tabIndex] === 'Chain' && selectedTarget && (
          <TargetChainPanel target={selectedTarget} joins={joins} embedded />
        )}

        {/* Code tab */}
        {tabs[tabIndex] === 'Code' && (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              fontFamily: '"Fira Code", "JetBrains Mono", Consolas, monospace',
              fontSize: 12,
              lineHeight: 1.7,
              bgcolor: '#1e293b',
              color: '#e2e8f0',
              height: '100%',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {activeSql || '-- No SQL generated yet. Generate mappings first.'}
          </Box>
        )}

        {/* Summary tab */}
        {tabs[tabIndex] === 'Summary' && (
          <Box sx={{ p: 2 }}>
            {selectedTarget ? (
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {selectedTarget.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  {selectedTarget.stepCount} column mappings ·{' '}
                  {selectedTarget.directCount} direct, {selectedTarget.expressionCount} expressions
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                  Average confidence: {Math.round(selectedTarget.avgConfidence * 100)}%
                </Typography>
                {selectedTarget.description && (
                  <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.5 }}>
                    {selectedTarget.description}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a target table to see its summary.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PipelineBottomPanel;
